import AbstractAsset from '@/editor/assets/abstract.asset'
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js'
import { DRACOLoader } from 'three/addons/loaders/DRACOLoader.js'
import {
  DEFAULT_D3_WIREFRAME_PROPS,
  getModelPlacementDefaults,
  updateWireframeModel
} from '@/editor/wireframeModel'
import { applyMediaMaterialMode } from '@/editor/materials/mediaMaterialMode'
// import { DEFAULT_DURATION } from '@/etc/consts'

const fallbackZeroFrame = (settings = {}) => {
  const safeWidth = Number(settings?.width) || Number(settings?.aspect?.w) || 16
  const safeHeight = Number(settings?.height) || Number(settings?.aspect?.h) || 9
  const aspect = safeWidth > 0 && safeHeight > 0 ? safeWidth / safeHeight : 16 / 9
  const height = 2 * Math.tan((35 * Math.PI / 180) / 2) * 100
  return {
    width: height * aspect,
    height,
  }
}

const usableZeroFrame = (zeroFrame, settings) => {
  const width = Number(zeroFrame?.width)
  const height = Number(zeroFrame?.height)
  if (Number.isFinite(width) && width > 0 && Number.isFinite(height) && height > 0) {
    return { width, height }
  }
  return fallbackZeroFrame(settings)
}

export default class D3Asset extends AbstractAsset {
  constructor(uuid) {
    super(uuid)
    const isModelAsset = new.target === D3Asset
    this._type = 'd3'
    this._props = {
      ...this._props,
      'position.x': 0,
      'position.y': 0,
      'position.z': 0,
      'scale.x': 1,
      'scale.y': 1,
      'scale.z': 1,
      'rotation.x': 0,
      'rotation.y': 0,
      'rotation.z': 0,
      opacity: 1,
      ...(isModelAsset ? DEFAULT_D3_WIREFRAME_PROPS : {}),
    }
  }

  get D() {
    return 3
  }

  get resizable() { return true }

  get PROPS() {
    const props = {
      position: {
        // machine-readable
        type: 'object',
        name: 'Position', // human readable
        tool: 'Translate3D',
        fields: {
          x: { type: 'number', name: 'X' },
          y: { type: 'number', name: 'Y' },
          z: { type: 'number', name: 'Z' },
        },
      },
      scale: {
        type: 'object',
        name: 'Scale',
        tool: 'Scale3D',
        fields: {
          x: { type: 'number', name: 'X' },
          y: { type: 'number', name: 'Y' },
          z: { type: 'number', name: 'Z' },
        },
      },
      rotation: {
        type: 'object',
        name: 'Rotation',
        tool: 'Rotate3D',
        fields: {
          x: { type: 'number', name: 'X' },
          y: { type: 'number', name: 'Y' },
          z: { type: 'number', name: 'Z' },
        },
      },
      opacity: {
        type: 'number',
        name: 'Opacity',
        normalized: true,
        tool: 'OpacitySlider',
      },
    }
    if (this.type !== 'd3') return props
    return {
      ...props,
      render: {
        type: 'object',
        name: 'Render',
        fields: {
          mode: {
            type: 'string',
            name: 'Mode',
            options: [
              { value: 'wireframe', label: 'Wireframe' },
              { value: 'wireframe+ghost', label: 'Wire + Ghost' },
              { value: 'solid', label: 'Solid' },
            ],
          },
        },
      },
      wireframe: {
        type: 'object',
        name: 'Wireframe',
        fields: {
          style: {
            type: 'string',
            name: 'Style',
            options: [
              { value: 'edges', label: 'Edges' },
              { value: 'mesh', label: 'Mesh' },
            ],
          },
          color: {
            type: 'color',
            name: 'Color',
            tool: 'ColorSelector',
          },
          opacity: {
            type: 'number',
            name: 'Opacity',
            normalized: true,
          },
          threshold: {
            type: 'number',
            name: 'Threshold',
            min: 0,
            max: 180,
            step: 1,
          },
        },
      },
    }
  }

  loadFromFile(file) {
    const mime = file?.type || 'model/gltf-binary'
    this._asset.blob = new Blob([file], { type: mime })
    this._asset.mime = mime
    return new Promise(resolve => {
      const loader = new GLTFLoader()
      const dracoLoader = new DRACOLoader()
      dracoLoader.setDecoderPath('/vendor/draco/')
      loader.setDRACOLoader(dracoLoader)
      const url = URL.createObjectURL(this._asset.blob)
      const done = () => {
        try { URL.revokeObjectURL(url) } catch (e) { /* noop */ }
        resolve()
      }
      loader.load(
        url,
        (gltf) => {
          this._asset.meta.name = file.name
          this._asset.gltf = gltf
          this._asset.ready = true
          this.emit('ready')
          done()
        },
        (progress) => {
          this.dbg.info('progress load', progress)
        },
        (err) => {
          this.dbg.error('load error', err)
          done()
        }
      )
    })
  }
  /*
  async loadFromFile(file) {
    await super.loadFromFile(file)
    this._asset.meta.name = file.name
    await this.loadInfo()
    this._asset.ready = true
    this.emit('ready')
  }
  */

  initAnimationD2(settings, zeroFrame) {
    const animation = super.initAnimation(settings)
    this.dbg.log('zf', zeroFrame, this._asset.meta)
    let scale = 1
    const frame = usableZeroFrame(zeroFrame, settings)
    const mw = Number(this._asset?.meta?.width)
    const mh = Number(this._asset?.meta?.height)
    const hasDims = Number.isFinite(mw) && mw > 0 && Number.isFinite(mh) && mh > 0
    if (
      !hasDims ||
      (mw < frame.width &&
        mh < frame.height)
    ) {
      scale = 1
    } else {
      // not fits to screen, scale down
      scale = Math.min(
        frame.width / mw,
        frame.height / mh
      )
    }
    if (!Number.isFinite(scale) || scale <= 0) scale = 1
    // Apply fit-to-screen scale and opacity to defaults so V2 (no keys) renders correctly
    this._props['scale.x'] = scale
    this._props['scale.y'] = scale
    this._props.opacity = 1
    if (animation.keyframes[0]) {
      animation.keyframes[0].props['scale.x'] = scale
      animation.keyframes[0].props['scale.y'] = scale
      animation.keyframes[0].easing['scale.x'] = 'Linear.None'
      animation.keyframes[0].easing['scale.y'] = 'Linear.None'
      animation.keyframes[0].delay['scale.x'] = 0
      animation.keyframes[0].delay['scale.y'] = 0
      animation.keyframes[0].props.opacity = 1
      animation.keyframes[0].easing.opacity = 'Linear.None'
      animation.keyframes[0].delay.opacity = 0
    }

    if (animation.keyframes[1]) animation.keyframes[1].props.opacity = 1
    return animation
  }



  initAnimation(settings, zeroFrame) {
    if (this.type === 'd3') {
      const placement = getModelPlacementDefaults(this._asset.gltf?.scene, zeroFrame)
      this._props = {
        ...this._props,
        ...placement,
        opacity: 1,
      }
    }
    const animation = super.initAnimation(settings)
    this.dbg.log('zf', zeroFrame, this._asset.meta)
    if (animation.keyframes[0]) {
      animation.keyframes[0].props.opacity = 1
      animation.keyframes[0].easing.opacity = 'Linear.None'
      animation.keyframes[0].delay.opacity = 0
    }

    // animation.keyframes[1].props.opacity = 1
    // animation.keyframes[1].easing.opacity = 1
    // animation.keyframes[1].delay.opacity = length

    if (animation.keyframes[1]) animation.keyframes[1].props.opacity = 1
    return animation
  }

  updateFrame(time, localTime, frame, zf, isPlay, propsUp) {
    this.dbg.log('updateFrame', time, localTime, frame)
    // const props = super.updateFrame(time, localTime, frame)
    const props = typeof propsUp === 'undefined'
      ? super.updateFrame(time, localTime, frame)
      : propsUp
    if (!frame || !frame.animation || !frame.mesh || !frame.strip) return props
    if (localTime < 0 || localTime > frame.strip.length) {
      frame.mesh.visible = false
      frame.mesh.layers.set(2)
      return props
    }
    frame.mesh.visible = true
    frame.mesh.layers.set(1)
    this.dbg.log('updateFrame', { time, localTime, frame, props })
    frame.mesh.position.set(props['position.x'], props['position.y'], props['position.z'])
    frame.mesh.scale.set(props['scale.x'], props['scale.y'], props['scale.z'])
    frame.mesh.rotation.x = props['rotation.x']
    frame.mesh.rotation.y = props['rotation.y']
    frame.mesh.rotation.z = props['rotation.z']
    if (this.type === 'd3') updateWireframeModel(frame.mesh, props)
    else if (frame.mesh.material) {
      applyMediaMaterialMode(frame.mesh.material, frame._mediaAlphaInfo, props.opacity)
    }
    return props
  }

  toolUpdate(update) {
    const ret = super.toolUpdate(update)
    if (update.tool === 'Translate2D') {
      ret['position.x'] = update.position.x
      ret['position.y'] = update.position.y
      // ret['position.z'] = update.position.z
    }
    if (update.tool === 'Translate3D') {
      ret['position.x'] = update.position.x
      ret['position.y'] = update.position.y
      ret['position.z'] = update.position.z
    }
    if (update.tool === 'Scale2D') {
      ret['scale.x'] = update.scale.x
      ret['scale.y'] = update.scale.y
      // ret['scale.z'] = update.scale.z
    }
    if (update.tool === 'Scale3D') {
      ret['scale.x'] = update.scale.x
      ret['scale.y'] = update.scale.y
      ret['scale.z'] = update.scale.z
    }
    if (update.tool === 'Rotate2D') {
      this.dbg.info('rotation update', update)
      // ret['rotation.x'] = update.rotation.x
      // ret['rotation.y'] = update.rotation.y
      ret['rotation.z'] = update.rotation.z
    }
    if (update.tool === 'Rotate3D') {
      this.dbg.info('rotation update', update)
      ret['rotation.x'] = update.rotation.x
      ret['rotation.y'] = update.rotation.y
      ret['rotation.z'] = update.rotation.z
    }
    if (update.tool === 'OpacitySlider') {
      ret.opacity = update.opacity
    }
    return ret
  }
}
